import { motion } from "framer-motion";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import { articles } from "@/seo/articles";
import { articlesEn } from "@/seo/articlesEn";
import { useLang, useTr } from "@/i18n";

export default function BlogPost() {
  const { slug } = useParams();
  const lang = useLang();
  const tr = useTr();
  const isEn = lang === "en";
  const article = (isEn ? articlesEn : articles).find((a) => a.slug === slug);

  if (!article) {
    return (
      <section className="pt-48 pb-32 text-center px-6">
        <h1 className="font-display text-3xl gold-text font-bold">{tr("blogpost.notFound", "Articolo non trovato")}</h1>
        <Link
          to={isEn ? "/en/blog" : "/blog"}
          className="inline-flex items-center gap-2 text-sm text-[#E2B857] mt-6 hover:text-[#F7DE8B] transition-colors"
        >
          <ArrowLeft size={16} /> {tr("blogpost.back", "Torna al blog")}
        </Link>
      </section>
    );
  }

  const itArticle = isEn ? articles.find((a) => a.slug === article.itSlug) : article;
  const enArticle = isEn ? article : articlesEn.find((a) => a.itSlug === article.slug);
  const postPath = `${isEn ? "/en/blog" : "/blog"}/${article.slug}`;
  const alternates = [
    { hreflang: "it", path: `/blog/${itArticle.slug}` },
    { hreflang: "x-default", path: `/blog/${itArticle.slug}` },
    ...(enArticle ? [{ hreflang: "en", path: `/en/blog/${enArticle.slug}` }] : []),
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.h1,
    description: article.description,
    datePublished: article.date,
    inLanguage: isEn ? "en" : "it",
    image: `https://djmagix.net${article.image}`,
    author: { "@type": "Person", name: "Luigi — DJ Magix", jobTitle: "DJ e producer", url: "https://djmagix.net/chi-sono" },
    dateModified: article.date,
    mainEntityOfPage: `https://djmagix.net${postPath}`,
  };

  return (
    <>
      <Seo title={article.title} description={article.description} path={postPath} jsonLd={jsonLd} lang={isEn ? "en" : "it"} alternates={alternates} />

      <section className="relative pt-40 pb-16 overflow-hidden">
        <img
          src={article.image}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-[#080808]" />
        <div className="relative z-10 max-w-3xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to={isEn ? "/en/blog" : "/blog"}
              data-testid="blog-back-link"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#BFA45D] hover:text-[#F7DE8B] transition-colors"
            >
              <ArrowLeft size={14} /> Blog
            </Link>
            <h1
              data-testid="blog-article"
              className="font-display text-3xl sm:text-4xl font-bold tracking-tight gold-text mt-5"
            >
              {article.h1}
            </h1>
            <p className="text-xs uppercase tracking-[0.25em] text-[#9F8A52] mt-4">
              {tr("blogpost.by", "di")} <span className="text-[#E2C882]">Luigi · DJ Magix</span> — {tr("blogpost.published", "pubblicato il")}{" "}
              {new Date(article.date).toLocaleDateString(isEn ? "en-GB" : "it-CH", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </p>
          </motion.div>
        </div>
      </section>

      <article className="max-w-3xl mx-auto px-6 pb-16">
        {article.blocks.map((b, i) => (
          <motion.div
            key={i}
            className="mb-8"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.5 }}
          >
            {b.h2 && (
              <h2 className="font-display text-xl sm:text-2xl font-semibold text-[#F7DE8B] mb-3">
                {b.h2}
              </h2>
            )}
            <p className="text-base text-[#E2C882]/85 leading-relaxed">{b.text}</p>
            {b.list && (
              <ul className="mt-4 space-y-2">
                {b.list.map((item) => (
                  <li key={item} className="flex gap-3 text-base text-[#E2C882]/85">
                    <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-[#E2B857]" />
                    {item}
                  </li>
                ))}
              </ul>
            )}
          </motion.div>
        ))}

        <aside data-testid="author-bio" className="gold-frame rounded-xl bg-[#111111] p-7 mt-4 flex flex-col sm:flex-row gap-5 items-start">
          <img
            src="/assets/logo-256.png"
            alt="Luigi — DJ Magix"
            width={64}
            height={64}
            className="h-16 w-16 rounded-full object-cover border border-[rgba(226,184,87,0.4)]"
          />
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#BFA45D]">{tr("blogpost.authorLabel", "Chi scrive")}</p>
            <p className="text-sm text-[#E2C882]/85 leading-relaxed mt-2">
              <b className="text-[#F7DE8B]">Luigi, in arte DJ Magix.</b> {tr("blogpost.authorBio", "DJ e producer con 25 anni di esperienza e oltre 1500 eventi tra Ticino, Svizzera e Nord Italia: matrimoni, feste di paese, club ed eventi aziendali. Le guide di questo blog nascono da ciò che vedo funzionare davvero in pista.")}
            </p>
          </div>
        </aside>

        <div className="gold-frame rounded-xl bg-[#111111] p-8 text-center mt-12">
          <p className="font-display text-xl font-semibold text-[#F7DE8B]">
            {tr("blogpost.ctaTitle", "Pianifichiamo il tuo evento?")}
          </p>
          <p className="text-sm text-[#E2C882]/80 mt-2">
            {tr("blogpost.ctaText", "Preventivo gratuito e senza impegno, risposta personale garantita.")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-6">
            <Link
              to={isEn ? "/en#contatti" : "/preventivo"}
              data-testid="blog-cta-quote"
              className="gold-btn rounded-full px-8 py-3.5 text-sm font-bold uppercase tracking-[0.15em]"
            >
              {tr("blogpost.ctaQuote", "Preventivo Gratuito")}
            </Link>
            <a
              href="tel:+41764411359"
              className="inline-flex items-center gap-2 rounded-full gold-frame bg-black/40 px-8 py-3.5 text-sm uppercase tracking-[0.15em] text-[#F7DE8B] hover:bg-[#E2B857]/10 transition-colors"
            >
              <Phone size={16} /> 076 441 13 59
            </a>
          </div>
        </div>
      </article>
    </>
  );
}
